import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Search, UserPlus, Mail, Building, Loader2, CheckCircle } from 'lucide-react'
import { buildApiUrl } from '@/config/api'
import toast from 'react-hot-toast'

interface LinkedInConnection {
  id: string
  linkedinId: string
  name: string
  email?: string
  company?: string
  position?: string
  profileUrl?: string
  avatar?: string
  isInvited: boolean
  invitedAt?: string
  joinedAt?: string
}

interface LinkedInInviteModalProps {
  isOpen: boolean
  onClose: () => void
  rfpId: string
  rfpTitle: string
}

export function LinkedInInviteModal({ isOpen, onClose, rfpId, rfpTitle }: LinkedInInviteModalProps) {
  const [connections, setConnections] = useState<LinkedInConnection[]>([])
  const [filteredConnections, setFilteredConnections] = useState<LinkedInConnection[]>([])
  const [selectedConnections, setSelectedConnections] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [customMessage, setCustomMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingConnections, setIsFetchingConnections] = useState(false)
  const [isSendingInvitations, setIsSendingInvitations] = useState(false)
  const [sentInvitations, setSentInvitations] = useState<string[]>([])
  const [showAddConnection, setShowAddConnection] = useState(false)
  const [newConnection, setNewConnection] = useState({
    name: '',
    email: '',
    company: '',
    position: ''
  })
  const [isAddingConnection, setIsAddingConnection] = useState(false)

  // Fetch LinkedIn connections when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchConnections()
    }
  }, [isOpen])

  // Filter connections based on search term
  useEffect(() => {
    const filtered = connections.filter(connection =>
      connection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      connection.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      connection.position?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    setFilteredConnections(filtered)
  }, [connections, searchTerm])

  const fetchConnections = async () => {
    setIsFetchingConnections(true)
    try {
      const response = await fetch(buildApiUrl('/api/linkedin-connections'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch connections')
      }

      const data = await response.json()
      setConnections(data.data.connections || [])
    } catch (error) {
      console.error('Error fetching connections:', error)
      toast.error('Failed to fetch LinkedIn connections')
    } finally {
      setIsFetchingConnections(false)
    }
  }

  const fetchLinkedInConnections = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(buildApiUrl('/api/linkedin-connections/fetch'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          accessToken: localStorage.getItem('linkedinAccessToken') || ''
        })
      })

      const data = await response.json()
      
      if (!response.ok) {
        if (data.limitation) {
          toast.error('LinkedIn connections API requires special permissions. You can add connections manually below.')
          return
        }
        if (data.requiresLinkedInAuth) {
          toast.error('Please connect your LinkedIn account first')
          return
        }
        throw new Error(data.error || 'Failed to fetch LinkedIn connections')
      }

      setConnections(data.data.connections || [])
      toast.success(data.message || `Fetched ${data.data.total} LinkedIn connections`)
    } catch (error) {
      console.error('Error fetching LinkedIn connections:', error)
      toast.error('Failed to fetch LinkedIn connections')
    } finally {
      setIsLoading(false)
    }
  }

  const handleConnectionToggle = (connectionId: string) => {
    setSelectedConnections(prev =>
      prev.includes(connectionId)
        ? prev.filter(id => id !== connectionId)
        : [...prev, connectionId]
    )
  }

  const handleSelectAll = () => {
    const availableConnections = filteredConnections.filter(conn => !conn.isInvited)
    setSelectedConnections(availableConnections.map(conn => conn.id))
  }

  const handleDeselectAll = () => {
    setSelectedConnections([])
  }

  const addConnection = async () => {
    if (!newConnection.name.trim()) {
      toast.error('Name is required')
      return
    }

    setIsAddingConnection(true)
    try {
      const response = await fetch(buildApiUrl('/api/linkedin-connections/add'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newConnection)
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add connection')
      }

      toast.success('Connection added successfully')
      setNewConnection({ name: '', email: '', company: '', position: '' })
      setShowAddConnection(false)
      await fetchConnections()
    } catch (error) {
      console.error('Error adding connection:', error)
      toast.error('Failed to add connection')
    } finally {
      setIsAddingConnection(false)
    }
  }

  const sendInvitations = async () => {
    if (selectedConnections.length === 0) {
      toast.error('Please select at least one connection')
      return
    }

    setIsSendingInvitations(true)
    const invitationPromises = selectedConnections.map(connectionId =>
      fetch(buildApiUrl('/api/linkedin-connections/invite'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          connectionId,
          rfpId,
          message: customMessage || undefined
        })
      })
    )

    try {
      const responses = await Promise.all(invitationPromises)
      const results = await Promise.all(responses.map(res => res.json()))

      const successful = results.filter(result => result.success)
      const failed = results.filter(result => !result.success)

      if (successful.length > 0) {
        setSentInvitations(successful.map(result => result.data.connectionId))
        toast.success(`Successfully sent ${successful.length} invitation(s)`)
      }

      if (failed.length > 0) {
        toast.error(`Failed to send ${failed.length} invitation(s)`)
      }

      // Refresh connections to update invitation status
      await fetchConnections()
      setSelectedConnections([])
      setCustomMessage('')
    } catch (error) {
      console.error('Error sending invitations:', error)
      toast.error('Failed to send invitations')
    } finally {
      setIsSendingInvitations(false)
    }
  }

  const handleClose = () => {
    setSelectedConnections([])
    setCustomMessage('')
    setSearchTerm('')
    setSentInvitations([])
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Invite LinkedIn Connections to View RFP
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Invite your LinkedIn connections to view "{rfpTitle}"
              </p>
            </div>
            <button 
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 text-xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col gap-4 p-6">
          {/* Search and Actions */}
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search connections by name, company, or position..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={fetchLinkedInConnections}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
              Fetch from LinkedIn
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowAddConnection(!showAddConnection)}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Manually
            </Button>
          </div>

          {/* Manual Connection Addition */}
          {showAddConnection && (
            <Card className="p-4 bg-blue-50 border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-3">Add Connection Manually</h3>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Full Name *"
                  value={newConnection.name}
                  onChange={(e) => setNewConnection(prev => ({ ...prev, name: e.target.value }))}
                />
                <Input
                  placeholder="Email"
                  type="email"
                  value={newConnection.email}
                  onChange={(e) => setNewConnection(prev => ({ ...prev, email: e.target.value }))}
                />
                <Input
                  placeholder="Company"
                  value={newConnection.company}
                  onChange={(e) => setNewConnection(prev => ({ ...prev, company: e.target.value }))}
                />
                <Input
                  placeholder="Position"
                  value={newConnection.position}
                  onChange={(e) => setNewConnection(prev => ({ ...prev, position: e.target.value }))}
                />
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={addConnection}
                  disabled={isAddingConnection || !newConnection.name.trim()}
                  size="sm"
                >
                  {isAddingConnection ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Add Connection
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowAddConnection(false)}
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          )}

          {/* Selection Actions */}
          {filteredConnections.length > 0 && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                Select All Available
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeselectAll}
              >
                Deselect All
              </Button>
              <div className="text-sm text-muted-foreground flex items-center">
                {selectedConnections.length} selected
              </div>
            </div>
          )}

          {/* Connections List */}
          <div className="flex-1 overflow-y-auto">
            {isFetchingConnections ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2">Loading connections...</span>
              </div>
            ) : filteredConnections.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {connections.length === 0 ? (
                  <div>
                    <UserPlus className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="font-medium mb-2">No LinkedIn connections found.</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      LinkedIn API access requires special permissions. You can:
                    </p>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>• Click "Add Manually" to add connections one by one</p>
                      <p>• Try "Fetch from LinkedIn" if you have API access</p>
                      <p>• Import connections from your LinkedIn export</p>
                    </div>
                  </div>
                ) : (
                  <p>No connections match your search.</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredConnections.map((connection) => (
                  <Card key={connection.id} className={`transition-colors ${
                    selectedConnections.includes(connection.id) ? 'ring-2 ring-primary' : ''
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedConnections.includes(connection.id)}
                          onChange={() => handleConnectionToggle(connection.id)}
                          disabled={connection.isInvited}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        
                        <div className="flex-1 flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            {connection.avatar ? (
                              <img
                                src={connection.avatar}
                                alt={connection.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <UserPlus className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium truncate">{connection.name}</h4>
                              {connection.isInvited && (
                                <Badge variant="secondary" className="text-xs">
                                  {connection.joinedAt ? 'Joined' : 'Invited'}
                                </Badge>
                              )}
                              {sentInvitations.includes(connection.id) && (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              )}
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              {connection.position && (
                                <span className="flex items-center gap-1">
                                  <UserPlus className="h-3 w-3" />
                                  {connection.position}
                                </span>
                              )}
                              {connection.company && (
                                <span className="flex items-center gap-1">
                                  <Building className="h-3 w-3" />
                                  {connection.company}
                                </span>
                              )}
                              {connection.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {connection.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Custom Message */}
          <div className="space-y-2">
            <label htmlFor="custom-message" className="block text-sm font-medium text-gray-700">
              Custom Message (Optional)
            </label>
            <Textarea
              id="custom-message"
              placeholder="Add a personal message to your invitation..."
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={sendInvitations}
              disabled={selectedConnections.length === 0 || isSendingInvitations}
            >
              {isSendingInvitations ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Send {selectedConnections.length} Invitation{selectedConnections.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
